import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { motion } from "framer-motion";
import { FaPlayCircle } from "react-icons/fa";
import Breadcrumb from "../components/BreadCrumb";
import VideoGrid from "../components/VideoGrid";
import { fetchGitHubContent } from "../utils/githubMediaUtils";

const Works = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { t } = useTranslation();
  const location = useLocation();
  const { serviceTitle, title, description, feature, works, _vId, bannerImage } = location.state || {};
  
  // State for dynamically loaded media
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Fetch related media from GitHub
  useEffect(() => {
    const fetchRelatedMedia = async () => {
      setLoading(true);
      try {
        // Try to determine the category from the serviceTitle
        if (serviceTitle) {
          // Convert serviceTitle to a folder name format (replace spaces with underscores)
          const folderName = serviceTitle.replace(/\s+/g, '_').toLowerCase();
          
          try {
            // Try to fetch media from the folder
            const media = await fetchGitHubContent(folderName);
            
            // Filter out folders and the current item
            const relatedMedia = media
              .filter(item => !item.isFolder && item.mediaType !== 'unknown')
              .filter(item => {
                // Skip the current item if we can identify it
                if (bannerImage && item.url === bannerImage) return false;
                return true;
              })
              .map(item => ({
                src: item.url,
                type: item.mediaType === 'video' ? 'video' : 'image',
                alt: item.name
              }));
            
            setMediaItems(relatedMedia);
          } catch (error) {
            console.warn(`Could not fetch media from folder ${folderName}:`, error);
            
            // Try the "works" folder as fallback
            try {
              const worksMedia = await fetchGitHubContent('works');
              const relatedMedia = worksMedia
                .filter(item => !item.isFolder && item.mediaType !== 'unknown')
                .filter(item => {
                  if (bannerImage && item.url === bannerImage) return false;
                  return true;
                })
                .map(item => ({
                  src: item.url,
                  type: item.mediaType === 'video' ? 'video' : 'image',
                  alt: item.name
                }));
              
              setMediaItems(relatedMedia);
            } catch (fallbackError) {
              console.error("Error fetching fallback media:", fallbackError);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching related media:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedMedia();
  }, [serviceTitle, bannerImage]);

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  const handleVideoClick = (videoId) => {
    // Clean up the video ID by splitting at '?'
    const cleanedVideoId = videoId.split("?")[0];
    setSelectedVideo(cleanedVideoId); // Set the selected video ID
  };

  const paths = [
    { label: t("homepage"), link: "/" },
    { label: t("portfolio"), link: "/portfolio" },
    { label: title || t("work_details"), link: "" },
  ];

  // Prepare slides for lightbox
  const lightboxSlides = mediaItems.map(item => {
    if (item.type === 'video') {
      return {
        type: 'video',
        width: 1280,
        height: 720,
        sources: [
          {
            src: item.src,
            type: `video/${item.src.split('.').pop().toLowerCase()}`
          }
        ]
      };
    }
    return { src: item.src };
  });

  return (
    <div className="w-full min-h-screen bg-white">
      <div className="pt-32">
        <Breadcrumb paths={paths} />
      </div>
      <div className="container mx-auto pb-32">
        <div className="lg:pt-10 pb-10 lg:pb-20 px-5">
          <div className="flex flex-col gap-5 leading-relaxed tracking-normal">
            <h1 className="font-semibold text-5xl lg:text-7xl">
              {serviceTitle || t("portfolio")}
            </h1>
            <p className="text-2xl lg:text-6xl">{title || ""}</p>
          </div>
        </div>

        {/* Main Feature Image/Video */}
        {bannerImage && (
          <div className="px-5 mb-12">
            <motion.div
              className="w-full rounded-lg overflow-hidden"
              initial={{ y: 48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {feature?.mediaType === 'video' ? (
                <video 
                  className="w-full h-auto max-h-[70vh] object-contain bg-black"
                  controls
                  autoPlay={false}
                >
                  <source src={bannerImage} type={`video/${bannerImage.split('.').pop().toLowerCase()}`} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={bannerImage} 
                  alt={title} 
                  className="w-full h-auto max-h-[70vh] object-contain"
                  onError={(e) => {
                    e.target.src = "/images/placeholder.jpg";
                  }}
                />
              )}
            </motion.div>
            
            {description && (
              <motion.div 
                className="mt-6 text-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <p>{description}</p>
              </motion.div>
            )}
          </div>
        )}

        {/* Related Media Grid */}
        {mediaItems.length > 0 && (
          <div className="px-5">
            <h2 className="text-2xl font-bold mb-6">{t("related_media")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mediaItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="relative rounded-lg overflow-hidden cursor-pointer"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.03 }}
                  onClick={() => openLightbox(index)}
                >
                  {item.type === 'video' ? (
                    <div className="relative">
                      <img
                        src={item.src.replace(/\.[^/.]+$/, ".jpg")} // Try to get thumbnail
                        alt={item.alt || "Video thumbnail"}
                        className="w-full h-60 object-cover"
                        onError={(e) => {
                          // If thumbnail doesn't exist, show a placeholder
                          e.target.src = "/images/placeholder.jpg";
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <FaPlayCircle className="text-white text-5xl opacity-80" />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={item.src}
                      alt={item.alt || `Image ${index + 1}`}
                      className="w-full h-60 object-cover"
                      onError={(e) => {
                        e.target.src = "/images/placeholder.jpg";
                      }}
                    />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Legacy Works Support */}
        {works && works.length > 0 && (
          <div>
            {works.map((work, idx) => (
              <div key={idx} className="p-6 rounded-lg mb-12">
                <div className="grid gap-4">
                  <motion.div
                    className="col-span-1"
                    initial={{ y: 48, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{
                      ease: "easeInOut",
                      duration: 0.75,
                      type: "spring",
                      stiffness: 300,
                    }}
                    whileHover={{ scale: 1.01 }}
                  >
                    {/* Show videos if available */}
                    <VideoGrid works={works} />
                  </motion.div>

                  {/* Two Images Full Width in Second Row with Animation */}
                  {work.images && work.images.length > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                      {work.images.map((image, imageId) => (
                        <motion.div
                          key={imageId}
                          whileHover={{ scale: 1.01 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                          }}
                        >
                          <motion.img
                            src={image.src}
                            alt="image"
                            className="rounded-lg object-cover h-full lg:h-[400px] w-full cursor-pointer"
                            initial={{ y: 48, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            transition={{ ease: "easeInOut", duration: 0.75 }}
                            onClick={() => openLightbox(imageId)}
                            onError={(e) => {
                              e.target.src = "/images/placeholder.jpg";
                            }}
                          />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No content message */}
        {!bannerImage && mediaItems.length === 0 && (!works || works.length === 0) && (
          <div className="px-5 py-20 text-center">
            <p className="text-gray-500">{t("no_content_available")}</p>
          </div>
        )}

        {/* Lightbox for Viewing Images */}
        <Lightbox
          open={isOpen}
          close={() => setIsOpen(false)}
          index={photoIndex}
          slides={lightboxSlides.length > 0 ? lightboxSlides : (
            works && works.length > 0 ? 
              works.flatMap((work) => work.images?.map((image) => ({ src: image.src })) || []) : 
              [{ src: bannerImage }]
          )}
          plugins={[Fullscreen, Slideshow, Thumbnails, Video, Zoom]}
        />
      </div>
    </div>
  );
};

export default Works;